/**
 * Flow Optimizer Tag Rules — rules list + editor for one layer.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useMemo, useState } from 'react';
import { Plus, Tag, Trash2 } from 'lucide-react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { FlowLayer, FlowTagRule } from '@trafficpulse/shared';

import { Button, Card, CardContent, CardHeader, CardTitle, LoadingSpinner } from '@/components/ui';
import { useToast } from '@/providers/ToastProvider';
import { getApiErrorMessage } from '@/lib/utils';
import {
  useBulkDeleteFlowTagRules,
  useCreateFlowTagRule,
  useDeleteFlowTagRule,
  useFlowTagRules,
  useReorderFlowTagRules,
  useUpdateFlowTagRule,
  type CreateFlowTagRuleInput,
} from '@/hooks';
import { UNGROUPED_KEY } from '@/pages/admin/ListTagRulesPage/constants';
import { overridesInUse, TagRuleScopeBar, useTagRuleScope } from '@/features/tag-rule-scope';

import { FlowSortableRuleRow } from './FlowSortableRuleRow';
import { FlowTagRuleFormModal } from './FlowTagRuleFormModal';
import { groupFlowRules } from './flow-tag-format';

export function FlowTagRuleTabContent({ layer }: { layer: FlowLayer }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const { success: toastSuccess, error: toastError } = useToast();
  const { data: rules, isLoading } = useFlowTagRules(layer);
  const { scope, setScope } = useTagRuleScope();
  const inUseScopes = useMemo(() => overridesInUse(rules), [rules]);
  const createRule = useCreateFlowTagRule(layer);
  const updateRule = useUpdateFlowTagRule(layer);
  const deleteRule = useDeleteFlowTagRule(layer);
  const bulkDelete = useBulkDeleteFlowTagRules(layer);
  const reorderRule = useReorderFlowTagRules(layer);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const grouped = useMemo(() => (rules ? groupFlowRules(rules) : []), [rules]);
  const availableGroupKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const r of rules ?? []) {
      const k = (r.groupKey ?? '').trim();
      if (k) keys.add(k);
    }
    return [...keys].sort();
  }, [rules]);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !rules) return;
      const group = grouped.find((g) => g.rules.some((r) => r.id === active.id));
      if (!group || group.rules.length < 2) return;
      const oldIndex = group.rules.findIndex((r) => r.id === active.id);
      const newIndex = group.rules.findIndex((r) => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(group.rules, oldIndex, newIndex);
      const updates = reordered.map((r, i) => ({ id: r.id, orderIndex: reordered.length - 1 - i }));
      try {
        await reorderRule.mutateAsync(updates);
        toastSuccess('Order updated', 'Top = checked first.');
      } catch (e) {
        toastError('Reorder failed', getApiErrorMessage(e, 'Could not reorder'));
      }
    },
    [grouped, rules, reorderRule, toastSuccess, toastError],
  );

  const handleSubmit = async (dto: CreateFlowTagRuleInput) => {
    try {
      if (editingId) {
        // A rule's layer is immutable — the update endpoint rejects `layer`.
        const { layer: _layer, ...patch } = dto;
        await updateRule.mutateAsync({ id: editingId, dto: patch });
      } else await createRule.mutateAsync(dto);
      toastSuccess(editingId ? 'Rule updated' : 'Rule created', 'Saved.');
      setEditingId(null);
      setModalOpen(false);
    } catch (e) {
      toastError('Save failed', getApiErrorMessage(e, 'Could not save rule'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await deleteRule.mutateAsync(id);
      toastSuccess('Rule deleted', 'Removed.');
    } catch (e) {
      toastError('Delete failed', getApiErrorMessage(e, 'Could not delete'));
    }
  };

  const handleToggle = async (rule: FlowTagRule) => {
    try {
      await updateRule.mutateAsync({ id: rule.id, dto: { enabled: !rule.enabled } });
    } catch (e) {
      toastError('Update failed', getApiErrorMessage(e, 'Could not toggle'));
    }
  };

  const selectedCount = selectedIds.size;
  const handleSelect = (id: string, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const handleDeleteSelected = async () => {
    if (!selectedCount || !window.confirm(`Delete ${selectedCount} rule(s)?`)) return;
    try {
      const n = await bulkDelete.mutateAsync([...selectedIds]);
      toastSuccess('Rules deleted', `${n} removed.`);
      setSelectedIds(new Set());
    } catch (e) {
      toastError('Delete failed', getApiErrorMessage(e, 'Could not delete'));
    }
  };

  const editingRule = editingId ? (rules?.find((r) => r.id === editingId) ?? null) : null;
  // The enabled, UNGROUPED no-stats rule already on this layer — the one the
  // backend would refuse a duplicate of. Excludes the rule being edited, so
  // reopening it doesn't report itself as a conflict.
  const noStatsHolder = useMemo(
    () =>
      (rules ?? []).find(
        (r) =>
          r.appliesTo === 'no-stats' &&
          r.enabled !== false &&
          !r.groupKey &&
          r.id !== editingId,
      ) ?? null,
    [rules, editingId],
  );

  return (
    <>
      <TagRuleScopeBar scope={scope} onScopeChange={setScope} inUse={inUseScopes} />

      <Card>
        <CardHeader className="border-b bg-card">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-base">Flow optimisation rules · {layer}</CardTitle>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {rules?.length ?? 0} rule{rules?.length === 1 ? '' : 's'} · tag by raw-score ranking + AND metrics
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {selectedCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={bulkDelete.isPending}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="mr-1.5 h-4 w-4" /> Delete selected
                </Button>
              )}
              <Button onClick={() => { setEditingId(null); setModalOpen(true); }}>
                <Plus className="mr-2 h-4 w-4" /> New rule
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-20"><LoadingSpinner size="lg" /></div>
          ) : rules && rules.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <div className="divide-y">
                {grouped.map((group) => (
                  <div key={group.key} className="border-b border-border last:border-b-0">
                    <div className="border-b border-border/50 bg-muted/20 px-6 py-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.label}
                      </span>
                      {group.key !== UNGROUPED_KEY && (
                        <span className="ml-2 text-xs text-muted-foreground">· drag to reorder (top = checked first)</span>
                      )}
                    </div>
                    <SortableContext items={group.rules.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                      {group.rules.map((rule) => (
                        <FlowSortableRuleRow
                          key={rule.id}
                          rule={rule}
                          onEdit={() => { setEditingId(rule.id); setModalOpen(true); }}
                          onDelete={() => handleDelete(rule.id)}
                          onToggleEnabled={() => handleToggle(rule)}
                          isReorderPending={reorderRule.isPending}
                          selected={selectedIds.has(rule.id)}
                          onSelectChange={(c) => handleSelect(rule.id, c)}
                          scope={scope}
                        />
                      ))}
                    </SortableContext>
                  </div>
                ))}
              </div>
            </DndContext>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="rounded-full bg-muted p-5"><Tag className="h-12 w-12 text-muted-foreground" /></div>
              <h3 className="mt-4 text-lg font-medium">No ranking rules yet</h3>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                Tag {layer} by the optimizer's raw score — e.g. &quot;Top 10% → POTENTIAL&quot;.
              </p>
              <Button className="mt-6" onClick={() => setModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" /> Create first rule
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <FlowTagRuleFormModal
        open={modalOpen || !!editingId}
        layer={layer}
        onOpenChange={(open) => { if (!open) { setModalOpen(false); setEditingId(null); } }}
        initial={editingRule}
        onSubmit={handleSubmit}
        submitting={createRule.isPending || updateRule.isPending}
        availableGroupKeys={availableGroupKeys}
        noStatsHolder={noStatsHolder}
        overrideScope={scope}
      />
    </>
  );
}
